import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';
import { RefreshCw, CheckCircle, GripVertical, X, Plus, FileText, Film, Sparkles, ChevronDown, ChevronUp, BookOpen, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { useProject } from '../contexts/ProjectContext';

const API_BASE = '/api';

// Sortable Item Component for TOP 5
function SortableArticle({ article, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: article.key });
  const [isExpanded, setIsExpanded] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isReddit = article.category === 'REDDIT';
  const isCustom = article.isCustom === true;
  const subreddit = article.subreddit || (article.url && article.url.match(/reddit\.com\/r\/([^/]+)/)?.[1]);

  return (
    <div ref={setNodeRef} style={style} className={cn(
      "group relative bg-card border rounded-lg p-4 hover:shadow-md transition-all overflow-hidden", 
      isDragging && "shadow-lg z-50",
      isReddit ? "bg-red-50/30 border-red-200" : isCustom ? "bg-emerald-50/30 border-emerald-200" : "bg-card border-border"
    )}>
      <div className="flex flex-wrap items-start gap-3">
        {/* Drag Handle */}
        <button {...attributes} {...listeners} className="mt-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing flex-shrink-0">
          <GripVertical className="w-5 h-5" />
        </button>

        {/* Number Badge */}
        <div className="flex-shrink-0">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
            isReddit ? "bg-red-600 text-white shadow-sm" : "bg-primary text-primary-foreground"
          )}>
            {index + 1}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
             {article.category && (
                <span className={cn(
                  "px-1.5 py-0.5 text-[10px] font-bold rounded flex items-center gap-1 flex-shrink-0 uppercase tracking-wider border",
                  isReddit ? "bg-red-100 text-red-800 border-red-200" : isCustom ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-blue-100 text-blue-800 border-blue-200"
                )}>
                  {article.category}
                </span>
             )}

             {isReddit && subreddit && (
                <span className="px-1.5 py-0.5 bg-white text-red-700 border border-red-100 text-[10px] font-bold rounded flex-shrink-0 shadow-sm">
                    r/{subreddit.replace(/^r\//, '')}
                </span>
             )}

             <h3 className="font-semibold text-base line-clamp-2 break-words">{article.title}</h3>
          </div>
          <a href={article.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block break-all line-clamp-1 mb-2">
            {article.url}
          </a>
          <div className="flex items-center flex-wrap gap-2 text-xs mb-2">
            {article.viral_score && (
                <span className={cn("px-2 py-0.5 rounded-full font-bold whitespace-nowrap", 
                    parseInt(article.viral_score) >= 8 ? "bg-red-600 text-white" : 
                    parseInt(article.viral_score) >= 6 ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-700"
                )}>
                  Viral Score: {article.viral_score}/10
                </span>
            )}
             {article.score && !article.viral_score && (
               <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium whitespace-nowrap">
                 Score: {article.score}
               </span>
             )}
          </div>
          
          {(article.description || article.justification) && (
            <div className="relative">
              <p className={cn("text-xs text-gray-600 mb-2 transition-all", !isExpanded && "line-clamp-2")}>
                {article.description || article.justification}
              </p>
              <button 
                onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }} 
                className="text-[10px] text-primary hover:underline flex items-center gap-0.5 mb-1 focus:outline-none"
              >
                {isExpanded ? (
                  <>Mniej <ChevronUp className="w-3 h-3" /></>
                ) : (
                  <>Więcej <ChevronDown className="w-3 h-3" /></>
                )}
              </button>
            </div>
          )}

          {article.reel_hook && (
            <div className="bg-purple-50 p-2 rounded border border-purple-100 mt-2">
                <p className={cn("text-[10px] text-purple-800 font-medium transition-all", !isExpanded && "line-clamp-1")}>
                  🎬 Hook: {article.reel_hook}
                </p>
            </div>
          )}
        </div>

        {/* Remove Button */}
        <div className="ml-auto flex-shrink-0">
          <button onClick={() => onRemove(article.key)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-destructive/10 rounded" title="Usuń z TOP 5">
            <X className="w-4 h-4 text-destructive" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Backup Article Component
function BackupArticle({ article, index, onAddToTop5, onSendToBlog, onAddToReels }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isReddit = article.category === 'REDDIT';
  const subreddit = article.subreddit || (article.url && article.url.match(/reddit\.com\/r\/([^/]+)/)?.[1]);

  return (
    <div className={cn("bg-card border rounded-lg p-3 hover:shadow-sm transition-shadow overflow-hidden flex flex-col h-full relative", isReddit && "bg-orange-50/40 border-orange-200")}>
      <div className="flex flex-wrap items-start gap-3 flex-1 mb-2">
        <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold">
          <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold", isReddit ? "bg-orange-600 text-white" : "bg-muted text-muted-foreground")}>
            {index + 1}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
             {article.category && (
                <span className={cn("px-1.5 py-0.5 text-[10px] font-bold rounded flex items-center gap-1 whitespace-nowrap flex-shrink-0 uppercase tracking-wider",
                    isReddit ? "bg-orange-100 text-orange-800 border border-orange-200" : "bg-blue-100 text-blue-800 border border-blue-200"
                )}>
                  {article.category}
                </span>
             )}

             {isReddit && subreddit && (
                <span className="px-1.5 py-0.5 bg-white text-gray-700 border border-gray-200 text-[10px] font-bold rounded flex-shrink-0">
                    r/{subreddit.replace(/^r\//, '')}
                </span>
             )}
          </div>
          <h4 className="font-medium text-sm mb-1 line-clamp-2 break-words leading-snug">{article.title}</h4>
          <a href={article.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline block break-all line-clamp-1 mb-1.5">
            {article.url}
          </a>
          
          <div className="flex flex-wrap gap-2 mb-2">
            {article.viral_score && (
              <span className={cn("inline-block px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap",
                parseInt(article.viral_score) >= 8 ? "bg-red-100 text-red-700" : 
                parseInt(article.viral_score) >= 6 ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-700"
              )}>
                Viral: {article.viral_score}/10
              </span>
            )}
             {article.score && !article.viral_score && (
               <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-medium whitespace-nowrap">
                 Score: {article.score}
               </span>
             )}
          </div>

          {(article.description || article.justification) && (
            <div className="relative">
              <p className={cn("text-xs text-gray-600 mt-1 leading-relaxed transition-all", !isExpanded && "line-clamp-3")}>
                {article.description || article.justification}
              </p>
              <button 
                onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }} 
                className="text-[10px] text-primary hover:underline flex items-center gap-0.5 mt-1 focus:outline-none"
              >
                {isExpanded ? (
                  <>Mniej <ChevronUp className="w-3 h-3" /></>
                ) : (
                  <>Więcej <ChevronDown className="w-3 h-3" /></>
                )}
              </button>
            </div>
          )}

          {article.reel_hook && (
            <div className="mt-2 pt-2 border-t border-dashed border-gray-200">
               <p className={cn("text-[10px] text-purple-700 font-medium transition-all", !isExpanded && "line-clamp-2")}>
                 🎣 {article.reel_hook}
               </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-auto pt-3 border-t flex justify-end gap-2">
          <button onClick={() => onAddToTop5(article.key)} className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 hover:bg-green-100 rounded text-xs font-medium transition-colors" title="Dodaj do TOP 5">
            <Plus className="w-3 h-3" /> TOP 5
          </button>
          <button onClick={() => onSendToBlog(article)} className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded text-xs font-medium transition-colors" title="Wyślij do bloga">
            <FileText className="w-3 h-3" /> Blog
          </button>
          <button onClick={() => onAddToReels(article)} className="flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded text-xs font-medium transition-colors" title="Dodaj do Reels">
            <Film className="w-3 h-3" /> Reels
          </button>
      </div>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const pollIntervalRef = useRef(null);
  const blogPollIntervalRef = useRef(null);
  const { currentProject } = useProject();

  const [top5Articles, setTop5Articles] = useState([]);
  const [backupArticles, setBackupArticles] = useState([]);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [pollingStatus, setPollingStatus] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customTitle, setCustomTitle] = useState('');

  // Blog proposals state
  const [blogProposals, setBlogProposals] = useState([]);
  const [isGeneratingProposals, setIsGeneratingProposals] = useState(false);
  const [proposalsReady, setProposalsReady] = useState(false);

  // Filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  const [blogFormData, setBlogFormData] = useState({
    title: '',
    keyword: '',
    keywordsSecondary: '',
    length: 'medium',
    faq: ''
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load TOP 5 from localStorage and fetch articles when project is ready
  useEffect(() => {
    if (!currentProject) return;

    // Check if newsletter was just published - clear TOP 5 if so
    const published = localStorage.getItem('newsletter_published');
    if (published) {
      localStorage.removeItem('newsletter_published');
      localStorage.removeItem(`top5_${currentProject}`);
    }

    // Load saved TOP 5 from localStorage
    let savedTop5 = [];
    const saved = localStorage.getItem(`top5_${currentProject}`);
    if (saved) {
      try {
        savedTop5 = JSON.parse(saved);
      } catch (e) {
        savedTop5 = [];
      }
    }
    setTop5Articles(savedTop5);

    // Load saved blog proposals from localStorage (only if from today)
    const today = new Date().toISOString().split('T')[0];
    const savedProposals = localStorage.getItem(`blog_proposals_${currentProject}`);
    if (savedProposals) {
      try {
        const parsed = JSON.parse(savedProposals);
        const items = parsed?.date === today ? parsed.proposals : null;
        if (Array.isArray(items) && items.length > 0) {
          setBlogProposals(items);
          setProposalsReady(true);
        } else if (!parsed?.date) {
          // Legacy format (plain array) — clear it, it's stale
          localStorage.removeItem(`blog_proposals_${currentProject}`);
        }
      } catch (e) {
        // ignore
      }
    }

    // Fetch articles, passing saved top5 so they're excluded from backup
    fetchNewsletterData(savedTop5);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  const saveTop5 = (articles) => {
    if (!currentProject) return;
    localStorage.setItem(`top5_${currentProject}`, JSON.stringify(articles));
  };

  // Sprawdza czy tablica zawiera propozycje blogowe (mają article_url + blog_title)
  const isBlogProposalArray = (arr) =>
    Array.isArray(arr) && arr.length > 0 && arr[0]?.article_url !== undefined && arr[0]?.blog_title !== undefined;

  // Szuka blog_proposals w wielu możliwych lokalizacjach w danych z n8n/S3
  const extractBlogProposals = (data) => {
    if (!data) return null;
    const candidates = [
      // Format n8n: {output: [...proposals...]} — output jest TABLICĄ propozycji
      isBlogProposalArray(data?.output) && data.output,
      // Format n8n z tablicą zewnętrzną: [{data:{output:[...proposals...]}}]
      Array.isArray(data) && isBlogProposalArray(data[0]?.data?.output) && data[0].data.output,
      // Format: [{json:{output:[...]}}]
      Array.isArray(data) && isBlogProposalArray(data[0]?.json?.output) && data[0].json.output,
      // Format: {output:{blog_proposals:[...]}}
      isBlogProposalArray(data?.output?.blog_proposals) && data.output.blog_proposals,
      // Format: [{output:{blog_proposals:[...]}}]
      Array.isArray(data) && isBlogProposalArray(data[0]?.output?.blog_proposals) && data[0].output.blog_proposals,
      // Format: {blog_proposals:[...]}
      isBlogProposalArray(data?.blog_proposals) && data.blog_proposals,
      // Format: [{blog_proposals:[...]}]
      Array.isArray(data) && isBlogProposalArray(data[0]?.blog_proposals) && data[0].blog_proposals,
    ];
    return candidates.find(Boolean) || null;
  };

  const parseNewsletterResponse = (data) => {
    let allArticles = [];

    // Format po odpakowaniu PHP: {output:[blog_proposals], data:[{output:{news:[...]}}, ...reddit...]}
    // newsletter.php unwrapuje [{data:{output:[...],data:[...]}}] → {output:[...],data:[...]}
    if (data?.data && Array.isArray(data.data) && data.data[0]?.output?.news) {
      const output = data.data[0].output;
      allArticles = [
        ...(output.news || []),
        ...data.data.slice(1).filter(item => item.subreddit).map(item => ({
          title: item.title,
          url: item.url_overridden_by_dest || item.url,
          description: item.selftext || '',
          category: 'REDDIT',
          subreddit: item.subreddit,
          score: item.score || item.ups || 0,
          viral_score: String(item.score || item.ups || 0),
          reel_hook: null
        }))
      ];
    }
    // Główny format: [{output:{news:[...], reddit_top15:[], blog_proposals:[...]}}, ...surowe Reddit...]
    else if (Array.isArray(data) && data[0]?.output?.news) {
      const output = data[0].output;
      allArticles = [
        ...(output.news || []),
        ...data.slice(1).filter(item => item.subreddit).map(item => ({
          title: item.title,
          url: item.url_overridden_by_dest || item.url,
          description: item.selftext || '',
          category: 'REDDIT',
          subreddit: item.subreddit,
          score: item.score || item.ups || 0,
          viral_score: String(item.score || item.ups || 0),
          reel_hook: null
        }))
      ];
    }
    // Format: {output:{news:[...]}} — pojedynczy obiekt (n8n bez tablicy zewnętrznej)
    else if (data?.output?.news) {
      allArticles = data.output.news || [];
    }
    // Format: {news:[...], reddit_top15:[...]}
    else if (data?.news) {
      allArticles = [
        ...(Array.isArray(data.news) ? data.news : []),
        ...(Array.isArray(data.reddit_top15) ? data.reddit_top15.map(item => ({
          ...item,
          title: item.title_pl || item.title_original || item.title,
          description: item.summary,
          score: item.upvotes,
          category: 'REDDIT',
          url: item.url,
          viral_score: item.viral_score
        })) : [])
      ];
    }
    // Format: płaska tablica
    else if (Array.isArray(data)) {
      allArticles = data;
    }

    return { allArticles };
  };

  const fetchNewsletterData = async (existingTop5 = []) => {
    try {
      const response = await fetch(`${API_BASE}/newsletter.php`, { credentials: 'include' });
      if (response.status === 401) {
        window.location.href = '/';
        return;
      }
      const result = await response.json();
      if (result.success) {
        const { allArticles } = parseNewsletterResponse(result.data);

        // Extract blog proposals from the same newsletter response
        const proposals = extractBlogProposals(result.data);
        if (proposals && proposals.length > 0 && !proposalsReady) {
          applyBlogProposals(proposals);
        }

        if (allArticles.length > 0) {
          const processedArticles = allArticles.map((art, idx) => ({
            ...art,
            key: art.key || `art-${idx}-${Date.now()}`,
            viral_score: art.viral_score || art.score,
            description: art.description || art.justification || art.summary,
            category: art.category || (art.subreddit ? 'REDDIT' : 'WEB')
          }));

          const cats = [...new Set(processedArticles.map(a => a.category).filter(Boolean))].sort();
          setCategories(cats);

          const top5Urls = new Set(existingTop5.map(a => a.url).filter(Boolean));
          setBackupArticles(processedArticles.filter(a => !top5Urls.has(a.url)));

          toast.success(`Pobrano ${processedArticles.length} artykułów`);
        } else {
          toast.error('Brak artykułów w odpowiedzi z serwera');
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Błąd pobierania danych');
    }
    return false;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setTop5Articles((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);
        saveTop5(reordered);
        return reordered;
      });
      toast.success('Kolejność zmieniona');
    }
  };

  const removeFromTop5 = (key) => {
    const article = top5Articles.find(a => a.key === key);
    if (article) {
      setBackupArticles([article, ...backupArticles]);
      const newTop5 = top5Articles.filter(a => a.key !== key);
      setTop5Articles(newTop5);
      saveTop5(newTop5);
      toast.success('Usunięto z TOP 5');
    }
  };

  const addToTop5 = (key) => {
    if (top5Articles.length >= 5) {
      toast.error('TOP 5 pełny! Usuń najpierw jakiś artykuł.');
      return;
    }
    const article = backupArticles.find(a => a.key === key);
    if (article) {
      const newTop5 = [...top5Articles, article];
      setTop5Articles(newTop5);
      setBackupArticles(backupArticles.filter(a => a.key !== key));
      saveTop5(newTop5);
      toast.success('Dodano do TOP 5');
    }
  };

  const addCustomArticle = () => {
    if (!customUrl.trim()) {
      toast.error('Podaj URL artykułu');
      return;
    }
    try { new URL(customUrl.trim()); } catch {
      toast.error('Nieprawidłowy URL');
      return;
    }
    if (top5Articles.length >= 5) {
      toast.error('TOP 5 pełny! Usuń najpierw jakiś artykuł.');
      return;
    }
    const article = {
      key: `custom-${Date.now()}`,
      url: customUrl.trim(),
      title: customTitle.trim() || customUrl.trim(),
      category: 'WŁASNY',
      isCustom: true
    };
    const newTop5 = [...top5Articles, article];
    setTop5Articles(newTop5);
    saveTop5(newTop5);
    setCustomUrl('');
    setCustomTitle('');
    toast.success('Dodano własny artykuł do TOP 5');
  };

  const acceptNewsletter = async () => {
    if (top5Articles.length < 3 || top5Articles.length > 5) {
      toast.error('Wybierz od 3 do 5 artykułów!');
      return;
    }

    setIsAccepting(true);
    setPollingStatus('Wysyłanie...');

    try {
      const regularArticles = top5Articles.filter(a => !a.isCustom).map(a => a.url);
      const customArticles = top5Articles.filter(a => a.isCustom).map(a => ({ url: a.url, title: a.title }));

      const payload = {
        action: 'accept',
        articles: regularArticles,
        custom_articles: customArticles
      };
      
      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.status === 'processing') {
        toast.success('Newsletter wysłany do generowania!');
        setPollingStatus('🧠 Generowanie podsumowań... (2-3 min)');

        // Poll local draft-status endpoint
        pollIntervalRef.current = setInterval(async () => {
          try {
            const statusResponse = await fetch(`${API_BASE}/draft-status.php`, { credentials: 'include' });
            const statusData = await statusResponse.json();

            if (statusData.ready) {
              clearInterval(pollIntervalRef.current);
              toast.success('Draft gotowy! Przekierowuję...');
              navigate('/preview');
            }
          } catch (error) {
            console.error('Polling error:', error);
          }
        }, 10000); // Poll every 10 seconds

      } else {
        toast.success('Newsletter zaakceptowany!');
        setIsAccepting(false);
        setPollingStatus('');
      }
    } catch (error) {
      toast.error('Błąd podczas akceptacji newslettera');
      setIsAccepting(false);
      setPollingStatus('');
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (blogPollIntervalRef.current) clearInterval(blogPollIntervalRef.current);
    };
  }, []);

  const generateBlogProposals = async () => {
    setIsGeneratingProposals(true);
    setProposalsReady(false);
    try {
      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'generate-blog-proposals' })
      });
      if (response.ok) {
        toast.success('Agent AI analizuje artykuły...');
        pollBlogProposals();
      } else {
        toast.error('Błąd podczas uruchamiania agenta');
        setIsGeneratingProposals(false);
      }
    } catch (error) {
      toast.error('Błąd: ' + error.message);
      setIsGeneratingProposals(false);
    }
  };

  const saveBlogProposalsToStorage = (items) => {
    if (!currentProject) return;
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(`blog_proposals_${currentProject}`, JSON.stringify({ date: today, proposals: items }));
  };

  const applyBlogProposals = (proposals) => {
    const withState = proposals.map(p => ({ ...p, sent: p.sent ?? false }));
    setBlogProposals(withState);
    setProposalsReady(true);
    setIsGeneratingProposals(false);
    saveBlogProposalsToStorage(withState);
    toast.success(`Gotowe! ${withState.length} propozycji bloga`);
  };

  const pollBlogProposals = () => {
    if (blogPollIntervalRef.current) clearInterval(blogPollIntervalRef.current);
    const today = new Date().toISOString().split('T')[0];
    blogPollIntervalRef.current = setInterval(async () => {
      try {
        const statusResp = await fetch(`${API_BASE}/blog-proposals-status.php?date=${today}`, { credentials: 'include' });
        const statusData = await statusResp.json();
        if (statusData.ready && Array.isArray(statusData.proposals) && statusData.proposals.length > 0) {
          clearInterval(blogPollIntervalRef.current);
          applyBlogProposals(statusData.proposals);
        }
      } catch (error) {
        console.error('Blog proposals polling error:', error);
      }
    }, 10000);
  };

  const approveBlogArticle = async (proposal, index) => {
    try {
      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'send-to-blog',
          payload: {
            article_url: proposal.article_url,
            title: proposal.blog_title,
            keyword: proposal.keyword_primary,
            keywords_secondary: proposal.keyword_secondary,
            length: '1500-2000'
          }
        })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success('Wysłano do Baserow!');
        const updated = blogProposals.map((p, i) => i === index ? { ...p, sent: true } : p);
        setBlogProposals(updated);
        saveBlogProposalsToStorage(updated);
      } else {
        toast.error('Błąd: ' + (result.error || result.message || 'Nieznany błąd'));
      }
    } catch (error) {
      toast.error('Błąd: ' + error.message);
    }
  };

  const updateProposal = (index, field, value) => {
    const updated = blogProposals.map((p, i) => i === index ? { ...p, [field]: value } : p);
    setBlogProposals(updated);
    saveBlogProposalsToStorage(updated);
  };

  const openBlogModal = (article) => {
    setSelectedArticle(article);
    setBlogFormData({ title: article.title || '', keyword: '', keywordsSecondary: '', length: 'short', faq: '' });
    setShowBlogModal(true);
  };

  const closeBlogModal = () => {
    setShowBlogModal(false);
    setSelectedArticle(null);
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    if (!selectedArticle) return;

    const lengthMap = { 'mini': '500-800', 'short': '800-1200', 'medium': '1500-2000', 'long': '2500-3500' };

    try {
      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'send-to-blog',
          payload: {
            article_url: selectedArticle.url,
            title: blogFormData.title,
            keyword: blogFormData.keyword,
            keywords_secondary: blogFormData.keywordsSecondary,
            length: lengthMap[blogFormData.length],
            faq_questions: blogFormData.faq
          }
        })
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Artykuł wysłany do Airtable!');
        closeBlogModal();
      } else {
        toast.error('Błąd podczas wysyłania artykułu');
      }
    } catch (error) {
      toast.error('Błąd podczas wysyłania artykułu');
    }
  };

  const addToReels = async (article) => {
    try {
      const payload = {
        post_id: article.post_id || (article.key && article.key.includes('.') ? article.key.split('.')[1] : article.key),
        title: article.title,
        url: article.url || '#',
        key: article.key,
        reel_hook: article.reel_hook,
        viral_score: article.viral_score,
        category: article.category,
        description: article.description
      };

      const response = await fetch(`${API_BASE}/actions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action: 'add-to-reels', payload })
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Dodano do Reels! Kolejka: ${result.queue_length || '?'} artykułów`);
      } else {
        toast.error('Błąd podczas dodawania do reels');
      }
    } catch (error) {
      toast.error('Błąd: ' + error.message);
    }
  };

  // Filtering Logic
  const getFilteredArticles = (articles) => {
    if (filterCategory === 'all') return articles;
    return articles.filter(article => article.category === filterCategory);
  };

  const filteredBackupArticles = getFilteredArticles(backupArticles);

  return (
    <div className="container mx-auto p-4 lg:p-6 max-w-7xl">
      {/* Header */}
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold">Dashboard</h2>
          <p className="text-muted-foreground mt-1">Zarządzaj newsletterem i artykułami</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => fetchNewsletterData(top5Articles)} className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors">
            <RefreshCw className="w-4 h-4" />
            Odśwież
          </button>
          <button onClick={acceptNewsletter} disabled={isAccepting} className={cn("flex items-center gap-2 px-4 py-2 rounded-md transition-colors font-medium", isAccepting ? "bg-muted text-muted-foreground cursor-not-allowed" : "bg-primary text-primary-foreground hover:bg-primary/90")}>
            <CheckCircle className="w-4 h-4" />
            {pollingStatus || (isAccepting ? 'Generowanie...' : 'Akceptuj Newsletter')}
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-card border rounded-lg p-4 mb-6 flex flex-wrap items-center gap-4">
        <span className="text-sm font-medium text-muted-foreground">Filtruj wg kategorii:</span>
        
        <div className="flex flex-wrap gap-2">
            <button 
                onClick={() => setFilterCategory('all')} 
                className={cn("px-3 py-1 rounded-full text-xs font-bold transition-colors border", filterCategory === 'all' ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/50")}
            >
                WSZYSTKIE
            </button>
            {categories.map(cat => (
                <button 
                    key={cat}
                    onClick={() => setFilterCategory(cat)} 
                    className={cn("px-3 py-1 rounded-full text-xs font-bold transition-colors border uppercase", filterCategory === cat ? "bg-blue-100 text-blue-800 border-blue-200" : "bg-background text-muted-foreground border-border hover:border-blue-200")}
                >
                    {cat}
                </button>
            ))}
        </div>
      </div>

      {/* TOP 5 Section */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">TOP 5 Propozycje</h3>
          <span className="text-sm text-muted-foreground">{top5Articles.length}/5 artykułów</span>
        </div>

        {/* Add custom article form */}
        <div className="flex flex-wrap gap-2 mb-4">
          <input
            type="url"
            placeholder="https://twój-artykuł.pl/..."
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCustomArticle()}
            className="flex-1 min-w-[200px] px-3 py-2 bg-background border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <input
            type="text"
            placeholder="Tytuł (opcjonalnie)"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCustomArticle()}
            className="w-48 px-3 py-2 bg-background border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            onClick={addCustomArticle}
            className="flex items-center gap-1 px-3 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 text-sm font-medium transition-colors whitespace-nowrap"
          >
            <Plus className="w-4 h-4" /> Dodaj własny
          </button>
        </div>

        {top5Articles.length === 0 ? (
          <div className="bg-card border rounded-lg p-12 text-center">
            <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Brak artykułów w TOP 5. Dodaj z listy backup poniżej.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={top5Articles.map(a => a.key)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {top5Articles.map((article, idx) => (
                  <SortableArticle key={article.key} article={article} index={idx} onRemove={removeFromTop5} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </section>

      {/* Backup Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">Backup <span className="text-muted-foreground font-normal text-sm ml-2">(Filtrowane: {filteredBackupArticles.length} z {backupArticles.length})</span></h3>
          <span className="text-sm text-muted-foreground">Całkowita liczba: {backupArticles.length}</span>
        </div>

        {filteredBackupArticles.length === 0 ? (
          <div className="bg-card border rounded-lg p-12 text-center">
            <p className="text-muted-foreground">Brak artykułów spełniających kryteria filtrów</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredBackupArticles.map((article, idx) => (
              <BackupArticle key={article.key} article={article} index={idx} onAddToTop5={addToTop5} onSendToBlog={openBlogModal} onAddToReels={addToReels} />
            ))}
          </div>
        )}
      </section>

      {/* Blog Proposals Section */}
      <section className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-emerald-600" />
            <h3 className="text-xl font-bold">Propozycje bloga</h3>
          </div>
          <button
            onClick={generateBlogProposals}
            disabled={isGeneratingProposals}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-md transition-colors font-medium text-sm",
              isGeneratingProposals
                ? "bg-muted text-muted-foreground cursor-not-allowed"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
            )}
          >
            {isGeneratingProposals ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Analizuję...</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Generuj propozycje</>
            )}
          </button>
        </div>

        {isGeneratingProposals && !proposalsReady && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-8 text-center">
            <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-3" />
            <p className="text-emerald-800 font-medium">Analizuję artykuły... (30-90 sek)</p>
            <p className="text-emerald-600 text-sm mt-1">Agent AI wybiera najlepsze tematy i generuje tytuły SEO</p>
          </div>
        )}

        {blogProposals.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {blogProposals.map((proposal, idx) => (
              <div
                key={idx}
                className={cn(
                  "bg-card border rounded-lg p-4 flex flex-col gap-3 transition-all",
                  proposal.sent ? "opacity-60 border-gray-200" : "border-emerald-200 hover:shadow-md"
                )}
              >
                <div>
                  <label className="block text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Tytuł SEO</label>
                  <div
                    contentEditable={!proposal.sent}
                    suppressContentEditableWarning
                    onBlur={(e) => updateProposal(idx, 'blog_title', e.currentTarget.textContent)}
                    className={cn(
                      "text-sm font-semibold leading-snug min-h-[40px] outline-none rounded px-1 -mx-1",
                      !proposal.sent && "hover:bg-emerald-50 focus:bg-emerald-50 focus:ring-1 focus:ring-emerald-300"
                    )}
                  >
                    {proposal.blog_title}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Słowo kluczowe główne</label>
                  <input
                    type="text"
                    value={proposal.keyword_primary}
                    onChange={(e) => updateProposal(idx, 'keyword_primary', e.target.value)}
                    disabled={proposal.sent}
                    className="w-full px-2 py-1 text-xs bg-background border rounded focus:outline-none focus:ring-1 focus:ring-emerald-300"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Słowo kluczowe LSI</label>
                  <input
                    type="text"
                    value={proposal.keyword_secondary}
                    onChange={(e) => updateProposal(idx, 'keyword_secondary', e.target.value)}
                    disabled={proposal.sent}
                    className="w-full px-2 py-1 text-xs bg-background border rounded focus:outline-none focus:ring-1 focus:ring-emerald-300"
                  />
                </div>

                <a
                  href={proposal.article_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-primary hover:underline line-clamp-1 break-all"
                  title={proposal.article_url}
                >
                  {proposal.article_url}
                </a>

                <button
                  onClick={() => approveBlogArticle(proposal, idx)}
                  disabled={proposal.sent}
                  className={cn(
                    "mt-auto w-full py-1.5 rounded text-xs font-bold transition-colors",
                    proposal.sent
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  )}
                >
                  {proposal.sent ? 'Wysłano ✓' : 'Zatwierdź'}
                </button>
              </div>
            ))}
          </div>
        )}

        {!isGeneratingProposals && blogProposals.length === 0 && (
          <div className="bg-card border rounded-lg p-10 text-center text-muted-foreground">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Kliknij "Generuj propozycje" aby agent AI wybrał 7 artykułów do bloga</p>
          </div>
        )}
      </section>

      {/* Blog Modal */}
      {showBlogModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && closeBlogModal()}>
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-primary text-primary-foreground px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">Dodaj artykuł do kolejki bloga</h2>
              <button onClick={closeBlogModal} className="hover:bg-primary/90 p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleBlogSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Propozycja tytułu *</label>
                <input type="text" required className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Jak AI zmienia marketing w 2025" value={blogFormData.title} onChange={(e) => setBlogFormData({ ...blogFormData, title: e.target.value })} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Słowo kluczowe główne *</label>
                <input type="text" required className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="AI marketing" value={blogFormData.keyword} onChange={(e) => setBlogFormData({ ...blogFormData, keyword: e.target.value })} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Słowa kluczowe drugorzędne</label>
                <input type="text" className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="automatyzacja marketingu, AI tools" value={blogFormData.keywordsSecondary} onChange={(e) => setBlogFormData({ ...blogFormData, keywordsSecondary: e.target.value })} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Długość artykułu *</label>
                <select required className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" value={blogFormData.length} onChange={(e) => setBlogFormData({ ...blogFormData, length: e.target.value })}>
                  <option value="mini">Mini (500-800 słów)</option>
                  <option value="short">Krótki (800-1200 słów)</option>
                  <option value="medium">Średni (1500-2000 słów)</option>
                  <option value="long">Długi (2500-3500 słów)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Propozycje pytań do FAQ</label>
                <textarea rows="4" className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Jak AI pomaga w marketingu?" value={blogFormData.faq} onChange={(e) => setBlogFormData({ ...blogFormData, faq: e.target.value })}></textarea>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={closeBlogModal} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors">
                  Anuluj
                </button>
                <button type="submit" className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors">
                  Wyślij do Airtable
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
